import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { logoutUser } from '../../store/actions/auth-actions';
import Container from '../UI/Container/Container';
import { Roles } from '../../helpers/consts/roles';

import { Layout, Menu, Button, Drawer, Select } from 'antd';
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  HomeOutlined,
  LogoutOutlined,
  TeamOutlined,
  CarOutlined,
  UserOutlined,
  PicRightOutlined,
  PictureOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons';

import { fetchCarHouseById } from '../../store/actions/carHouse-actions';

import classes from './Sidebar.module.css';
import logo from '../../assets/img/Logo.svg';

import { useTranslation } from 'react-i18next';

const { Header, Sider, Content } = Layout;

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [currentLink, setCurrentLink] = useState(false);
  const loggedInUser = useSelector((state) => state.auth.loggedInUser);
  const carHouse = useSelector((state) => state.carHouse.carHouse);

  const { t, i18n } = useTranslation();

  let navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchCarHouseData = async () => {
      try {
        if (
          (loggedInUser.role.name === Roles.CARHOUSEADMIN ||
            loggedInUser.role.name === Roles.USER) &&
          loggedInUser.carHouseId
        ) {
          await dispatch(fetchCarHouseById(loggedInUser.carHouseId));
        }
      } catch (error) {
        console.error('Error fetching car house data:', error);
      }
    };

    fetchCarHouseData();
  }, [loggedInUser.role.name, loggedInUser.carHouseId, dispatch]);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  const activeSubscription = carHouse?.subscriptions?.find(
    (subscription) => subscription.status === 'active'
  );

  const shouldShowCredits =
    (loggedInUser.role.name === Roles.CARHOUSEADMIN ||
      loggedInUser.role.name === Roles.USER) &&
    carHouse &&
    activeSubscription?.paymentType === 'prepaid';

  const creditDisplay = shouldShowCredits ? (
    <div
      style={{
        padding: '10px 20px',
        width: '100%',
        textAlign: 'left',
        marginTop: '10px',
        background: '#3d996f',
        color: 'white',
      }}
    >
      <p style={{ margin: 0 }}>
        {t('subscriptions.credits')}: <strong>{carHouse.credits}</strong>
      </p>
    </div>
  ) : null;

  const rootRoutes = [
    {
      label: t('navigation.vehicles'),
      key: '/vehicles',
      icon: <CarOutlined />,
      onClick: () => {
        navigate('/vehicles');
      },
    },
    {
      label: t('navigation.carHouses'),
      key: '/car-houses',
      icon: <HomeOutlined />,
      onClick: () => {
        navigate('/car-houses');
      },
    },
    {
      label: t('navigation.admins'),
      key: '/admins',
      icon: <TeamOutlined />,
      onClick: () => {
        navigate('/admins');
      },
    },
    {
      label: t('navigation.tags'),
      key: '/tags',
      icon: <PicRightOutlined />,
      onClick: () => {
        navigate('/tags');
      },
    },
    {
      label: t('navigation.vehicleSpecifications'),
      key: '/vehicle-specifications',
      icon: <CarOutlined />,
      onClick: () => {
        navigate('/vehicle-specifications');
      },
    },
    {
      label: t('navigation.logos'),
      key: '/logos',
      icon: <PictureOutlined />,
      onClick: () => {
        navigate('/logos');
      },
    },
    {
      label: 'Image Requests',
      key: '/image-requests',
      icon: <PictureOutlined />,
      onClick: () => {
        navigate('/image-requests');
      },
    },
    {
      label: t('navigation.basicInformation'),
      key: '/basic-information',
      icon: <InfoCircleOutlined />,
      onClick: () => {
        navigate('/basic-information');
      },
    },
  ];

  const mainRoutes = [
    {
      label: t('navigation.vehicles'),
      key: '/vehicles',
      icon: <CarOutlined />,
      onClick: () => {
        navigate('/vehicles');
      },
    },
    {
      label: t('navigation.profile'),
      key: '/profile',
      icon: <UserOutlined />,
      onClick: () => {
        navigate('/profile');
      },
    },
  ];

  const userRoutes = [
    {
      label: t('navigation.vehicles'),
      key: '/vehicles',
      icon: <CarOutlined />,
      onClick: () => {
        navigate('/vehicles');
      },
    },
    {
      label: t('navigation.purchaseVehicles'),
      key: '/purchase-vehicles',
      icon: <CarOutlined />,
      onClick: () => {
        navigate('/purchase-vehicles');
      },
    },
    {
      label: t('navigation.logos'),
      key: '/logos',
      icon: <PictureOutlined />,
      onClick: () => {
        navigate('/logos');
      },
    },
    {
      label: t('navigation.profile'),
      key: '/profile',
      icon: <UserOutlined />,
      onClick: () => {
        navigate('/profile');
      },
    },
  ];

  const adminRoutes = [
    {
      label: t('navigation.vehicles'),
      key: '/vehicles',
      icon: <CarOutlined />,
      onClick: () => {
        navigate('/vehicles');
      },
    },
    {
      label: 'Image Requests',
      key: '/image-requests',
      icon: <PictureOutlined />,
      onClick: () => {
        navigate('/image-requests');
      },
    },
    {
      label: t('navigation.profile'),
      key: '/profile',
      icon: <UserOutlined />,
      onClick: () => {
        navigate('/profile');
      },
    },
  ];

  const carHouseAdminRoutes = [
    {
      label: t('navigation.vehicles'),
      key: '/vehicles',
      icon: <CarOutlined />,
      onClick: () => {
        navigate('/vehicles');
      },
    },
    {
      label: t('navigation.purchaseVehicles'),
      key: '/purchase-vehicles',
      icon: <CarOutlined />,
      onClick: () => {
        navigate('/purchase-vehicles');
      },
    },
    {
      label: t('navigation.users'),
      key: '/users',
      icon: <TeamOutlined />,
      onClick: () => {
        navigate('/users');
      },
    },
    {
      label: t('navigation.subscriptions'),
      key: '/subscriptions',
      icon: <FileTextOutlined />,
      onClick: () => {
        navigate(`${loggedInUser.carHouseId}/subscriptions`);
      },
    },
    {
      label: t('navigation.logos'),
      key: '/logos',
      icon: <PictureOutlined />,
      onClick: () => {
        navigate('/logos');
      },
    },
    {
      label: t('navigation.profile'),
      key: '/profile',
      icon: <UserOutlined />,
      onClick: () => {
        navigate('/profile');
      },
    },
  ];

  const roleMenu = () => {
    switch (loggedInUser.role.name) {
      case Roles.ROOT:
        return rootRoutes;
      case Roles.ADMIN:
        return adminRoutes;
      case Roles.CARHOUSEADMIN:
        return carHouseAdminRoutes;
      case Roles.USER:
        return userRoutes;
      default:
        return mainRoutes;
    }
  };

  const location = useLocation();
  const fullWidth = global.window.innerWidth;

  useEffect(() => {
    let link;
    if (location) {
      if (currentLink !== location.pathname) {
        link = `/${location.pathname.split('/')[1]}`;
        setCurrentLink(link);
      }
    }
    if (fullWidth <= 992) {
      setCollapsed(false);
    }
  }, [location, currentLink, loggedInUser.role.name, fullWidth]);

  const toggleTrueFalse = () => setCollapsed(!collapsed);
  const onClose = () => {
    setCollapsed(false);
  };

  const selectStyle = {
    width: 63,
    borderColor: '#d9d9d9',
    fontWeight: 500,
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Drawer
        placement="left"
        title=""
        onClose={onClose}
        destroyOnClose
        closable={false}
        open={collapsed}
        className="hideOnDesktop"
        bodyStyle={{ backgroundColor: '#ffffff', padding: '0' }}
        width={250}
      >
        <CloseOutlined
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            fontSize: '20px',
            cursor: 'pointer',
          }}
        />
        <Menu
          theme="light"
          mode="inline"
          items={roleMenu()}
          selectedKeys={[currentLink]}
          style={{ marginTop: '50px' }}
        />
        {creditDisplay}
      </Drawer>
      <Sider
        theme="light"
        breakpoint="lg"
        collapsedWidth="0"
        collapsed={collapsed}
        onBreakpoint={(broken) => {
          setCollapsed(broken);
        }}
        className="hideOnMobile"
      >
        <div className={`${classes.logo}`}>
          <img src={logo} alt="Logo" width={160} />
        </div>
        <Menu
          theme="light"
          mode="inline"
          items={roleMenu()}
          selectedKeys={[currentLink]}
        />
        {creditDisplay}
      </Sider>
      <Layout>
        <Header
          className="site-layout-background"
          style={{
            padding: '0px 30px 0px 0px',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {React.createElement(
            collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
            {
              className: 'trigger',
              onClick: toggleTrueFalse,
            }
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              color: 'black',
            }}
          >
            <Link to="/privacy-policy" style={{ fontSize: 14, color: 'black' }}>
              {t('navigation.privacyPolicy')}
            </Link>
            <Link to="/terms-of-use" style={{ fontSize: 14, color: 'black' }}>
              {t('navigation.termsOfUse')}
            </Link>
            <Select
              defaultValue={i18n.language}
              style={selectStyle}
              onChange={handleLanguageChange}
              value={i18n.language}
            >
              <Select.Option value="en">EN</Select.Option>
              <Select.Option value="de">DE</Select.Option>
              <Select.Option value="sr">SR</Select.Option>
            </Select>
            <Button
              icon={<LogoutOutlined />}
              onClick={() => {
                dispatch(logoutUser());
                navigate('/login', { replace: true });
              }}
            />
          </div>
        </Header>
        <Container>
          <Content className="site-layout-background">
            <Outlet />
          </Content>
        </Container>
      </Layout>
    </Layout>
  );
};

export default Sidebar;
